import React from 'react';
import './DatasetCard.css';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import {API_BASEURL} from "../../../Constants";

export type DatasetCardItem = {
    id: string;
    title: string;
    municipalityId?: number;
    municipalityTextId?: string;
    typCode?: string | null;
    typNazev?: string | null;
    bodyCode?: string | null;
    termFrom?: number | null;
    termTo?: number | null;
    logoUrl?: string | null;
    posledniAktualizace?: string | null;
    datasetPoznamka?: string | null;
    uplnyDataset?: boolean;
    urlDataset?: string | null;
    urlZdroj?: string | null;
};

type Props = {
    item: DatasetCardItem;
};
/** Karta datasetu: Název, Datum, Poznámka, uplnyDataset, Odkaz na dataset, Odkaz na zdroj. */
const DatasetCard = React.memo(({ item }: Props) => {
    const { t, i18n } = useTranslation('datasets');
    const hasDataset = Boolean(item.urlDataset);
    const hasZdroj = Boolean(item.urlZdroj);
    const isUplny = item.uplnyDataset === true;
    const defaultIncompleteNote = t('DatasetCard.incompleteDataNote');
    const poznamka = item.datasetPoznamka ?? (!isUplny ? defaultIncompleteNote : null);

    const currentLocale = i18n.language;
    let formattedDate = undefined;

    if (item.posledniAktualizace) {
        const dt = DateTime.fromISO(item.posledniAktualizace, { zone: 'Europe/Prague' });
        formattedDate = dt.setLocale(currentLocale).toLocaleString(DateTime.DATE_SHORT);
    }

    return (
        <article className="dataset-card">
            <div className="dataset-card-header">
                {item.logoUrl ? (
                    <div className="dataset-card-logo">
                        <img src={item.logoUrl} alt="" />
                    </div>
                ) : (
                    <div className="dataset-card-logo dataset-card-logo-placeholder" aria-hidden />
                )}
                <div className="dataset-card-title-wrap">
                    <h3 className="dataset-card-title">{item.title}</h3>
                    <span
                        className={`dataset-card-uplny dataset-card-uplny--${isUplny ? 'complete' : 'incomplete'}`}
                        aria-label={isUplny ? t('DatasetCard.completeDataset') : t('DatasetCard.incompleteDataset')}
                    >
                        {isUplny ? t('DatasetCard.completeDataset') : t('DatasetCard.incompleteDataset')}
                    </span>
                    {poznamka && (
                        <span className="dataset-card-meta dataset-card-poznamka">{poznamka}</span>
                    )}
                    {item.posledniAktualizace && (
                        <span className="dataset-card-meta dataset-card-date">
                            {t('DatasetCard.lastUpdate', {date: formattedDate})}
                        </span>
                    )}
                </div>
            </div>
            <div className="dataset-card-actions">
                {hasDataset && (
                    <a
                        href={`${API_BASEURL}${item.urlDataset!}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dataset-card-button"
                    >
                        {t('DatasetCard.processedData')}
                    </a>
                )}
                {hasZdroj && (
                    <a
                        href={item.urlZdroj!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dataset-card-zdroj"
                    >
                        {t('DatasetCard.sourceData')}
                    </a>
                )}
            </div>
        </article>
    );
});

DatasetCard.displayName = 'DatasetCard';

export default DatasetCard;
